// src/components/DocumentQrPanel.jsx
// QR生成（/admin/qr）の「顧客用PDF QR管理」タブ。
// 文書ごとに PDF と QR コードを持つ（複数登録できる）。
// PDF を差し替えても文書の URL と QR は変わらないため、配布・印刷済みの QR はその文書の最新PDFを開く。
import React, { useCallback, useEffect, useRef, useState } from "react";
import api from "../lib/api";
import "./DocumentQrPanel.css";

const apiError = (err, fallback) =>
  err?.response?.data?.error || err?.response?.data?.detail || fallback;

const formatDateTime = (s) => {
  if (!s) return "";
  const d = new Date(s);
  if (Number.isNaN(d.getTime())) return "";
  const p = (n) => String(n).padStart(2, "0");
  return `${d.getFullYear()}/${p(d.getMonth() + 1)}/${p(d.getDate())} ${p(d.getHours())}:${p(d.getMinutes())}`;
};

const DocumentQrPanel = () => {
  const [docs, setDocs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState("");
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");

  // 新規登録
  const [newTitle, setNewTitle] = useState("");
  const [newFile, setNewFile] = useState(null);
  const [creating, setCreating] = useState(false);
  const newFileRef = useRef(null);

  // 文書ごとの操作中フラグ（二重送信防止）とタイトル編集
  const [busyId, setBusyId] = useState(null);
  const [editingId, setEditingId] = useState(null);
  const [editTitle, setEditTitle] = useState("");
  const replaceInputs = useRef({});

  const notify = (msg) => {
    setError("");
    setMessage(msg);
  };
  const fail = (msg) => {
    setMessage("");
    setError(msg);
  };

  const loadDocs = useCallback(async () => {
    setLoading(true);
    setLoadError("");
    try {
      const res = await api.get("/core-pdf-qr/documents/");
      setDocs(Array.isArray(res.data) ? res.data : []);
    } catch (err) {
      setLoadError(apiError(err, "データを取得できませんでした。"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    loadDocs();
  }, [loadDocs]);

  const handleCreate = async (e) => {
    e.preventDefault();
    if (creating) return;
    if (!newTitle.trim()) {
      fail("資料タイトルを入力してください。");
      return;
    }
    if (!newFile) {
      fail("PDFファイルを選択してください。");
      return;
    }
    setCreating(true);
    const fd = new FormData();
    fd.append("title", newTitle.trim());
    fd.append("pdf_file", newFile);
    try {
      // Content-Type は指定しない（axios が boundary 付きで自動設定する）
      const res = await api.post("/core-pdf-qr/documents/", fd);
      setDocs((prev) => [...prev, res.data]);
      setNewTitle("");
      setNewFile(null);
      if (newFileRef.current) newFileRef.current.value = "";
      notify(`「${res.data.title}」を登録し、QRコードを作成しました。`);
    } catch (err) {
      fail(apiError(err, "登録に失敗しました。"));
    } finally {
      setCreating(false);
    }
  };

  const replaceDoc = (id, patch) => setDocs((prev) => prev.map((d) => (d.id === id ? patch : d)));

  const handleReplacePdf = async (doc, file) => {
    if (!file || busyId) return;
    const ok = window.confirm(
      `「${doc.title}」の PDF を「${file.name}」に差し替えます。\n` +
        "QRコードはそのままで、配布・印刷済みの QR を読んだ人にも新しい PDF が表示されます。"
    );
    if (!ok) return;
    setBusyId(doc.id);
    const fd = new FormData();
    fd.append("pdf_file", file);
    try {
      const res = await api.patch(`/core-pdf-qr/documents/${doc.id}/`, fd);
      replaceDoc(doc.id, res.data);
      notify(`「${res.data.title}」の PDF を差し替えました。`);
    } catch (err) {
      fail(apiError(err, "PDF の差し替えに失敗しました。"));
    } finally {
      setBusyId(null);
      const input = replaceInputs.current[doc.id];
      if (input) input.value = "";
    }
  };

  const startEdit = (doc) => {
    setEditingId(doc.id);
    setEditTitle(doc.title);
  };

  const handleSaveTitle = async (doc) => {
    if (busyId) return;
    const title = editTitle.trim();
    if (!title) {
      fail("資料タイトルを入力してください。");
      return;
    }
    if (title === doc.title) {
      setEditingId(null);
      return;
    }
    setBusyId(doc.id);
    const fd = new FormData();
    fd.append("title", title);
    try {
      const res = await api.patch(`/core-pdf-qr/documents/${doc.id}/`, fd);
      replaceDoc(doc.id, res.data);
      setEditingId(null);
      notify("資料タイトルを変更しました。");
    } catch (err) {
      fail(apiError(err, "タイトルの変更に失敗しました。"));
    } finally {
      setBusyId(null);
    }
  };

  const handleDelete = async (doc) => {
    if (busyId) return;
    const ok = window.confirm(
      `「${doc.title}」を削除します。\n` +
        "PDF と QRコードも削除され、配布・印刷済みのものを含め、この QR を読んでも PDF は開けなくなります。元に戻せません。"
    );
    if (!ok) return;
    setBusyId(doc.id);
    try {
      await api.delete(`/core-pdf-qr/documents/${doc.id}/`);
      setDocs((prev) => prev.filter((d) => d.id !== doc.id));
      notify(`「${doc.title}」を削除しました。`);
    } catch (err) {
      fail(apiError(err, "削除に失敗しました。"));
    } finally {
      setBusyId(null);
    }
  };

  return (
    <div className="dq-panel">
      <section className="dq-card">
        <h3 className="dq-heading">新しい文書を登録</h3>
        <p className="dq-note">
          PDF を登録すると、その文書専用の QRコードを作成します。文書ごとに別の QRコードになります。
        </p>
        <form className="dq-form" onSubmit={handleCreate}>
          <label className="dq-field">
            <span className="dq-label">資料タイトル</span>
            <input
              type="text"
              className="dq-input"
              value={newTitle}
              maxLength={200}
              onChange={(e) => setNewTitle(e.target.value)}
              placeholder="例：顧客用メニュー"
            />
          </label>
          <label className="dq-field">
            <span className="dq-label">PDFファイル</span>
            <input
              ref={newFileRef}
              type="file"
              accept="application/pdf,.pdf"
              className="dq-file"
              onChange={(e) => setNewFile(e.target.files?.[0] || null)}
            />
          </label>
          <div className="dq-actions">
            <button type="submit" className="dq-btn dq-btn-primary" disabled={creating || !newTitle.trim() || !newFile}>
              {creating ? "登録中…" : "登録してQRコードを作成"}
            </button>
          </div>
        </form>
      </section>

      {message && <p className="dq-msg dq-msg-ok" role="status">{message}</p>}
      {error && <p className="dq-msg dq-msg-err" role="alert">{error}</p>}

      <section>
        <h3 className="dq-heading">登録済みの文書</h3>
        {loading && docs.length === 0 && <p className="dq-state">読み込み中…</p>}
        {!loading && loadError && (
          <div className="dq-state">
            <p>{loadError}</p>
            <button type="button" className="dq-btn dq-btn-secondary" onClick={loadDocs}>
              再試行
            </button>
          </div>
        )}
        {!loading && !loadError && docs.length === 0 && (
          <p className="dq-state">登録されている文書はありません。上のフォームから PDF を登録してください。</p>
        )}

        <div className="dq-list">
          {docs.map((doc) => {
            const busy = busyId === doc.id;
            return (
              <article key={doc.id} className="dq-item">
                <div className="dq-item-qr">
                  {doc.qr_code_url ? (
                    <img src={doc.qr_code_url} alt={`${doc.title} の QRコード`} className="dq-qr-img" />
                  ) : (
                    <span className="dq-qr-none">QRコードなし</span>
                  )}
                </div>
                <div className="dq-item-body">
                  {editingId === doc.id ? (
                    <div className="dq-edit">
                      <input
                        type="text"
                        className="dq-input"
                        value={editTitle}
                        maxLength={200}
                        onChange={(e) => setEditTitle(e.target.value)}
                        aria-label="資料タイトル"
                      />
                      <div className="dq-actions">
                        <button type="button" className="dq-btn dq-btn-ghost" onClick={() => setEditingId(null)} disabled={busy}>
                          キャンセル
                        </button>
                        <button type="button" className="dq-btn dq-btn-primary" onClick={() => handleSaveTitle(doc)} disabled={busy}>
                          保存
                        </button>
                      </div>
                    </div>
                  ) : (
                    <h4 className="dq-item-title">{doc.title}</h4>
                  )}
                  <p className="dq-item-meta">
                    登録 {formatDateTime(doc.created_at) || "—"} ／ 更新 {formatDateTime(doc.updated_at)}
                  </p>

                  <div className="dq-item-links">
                    {doc.qr_code_url && (
                      <a href={doc.qr_code_url} download={`document_qr_${doc.id}.png`} className="dq-btn dq-btn-secondary">
                        QRコードをダウンロード
                      </a>
                    )}
                    {doc.pdf_url && (
                      <a href={doc.pdf_url} target="_blank" rel="noreferrer" className="dq-btn dq-btn-secondary">
                        PDFを確認
                      </a>
                    )}
                  </div>

                  <div className="dq-item-ops">
                    <label className={`dq-btn dq-btn-secondary${busy ? " dq-btn-disabled" : ""}`}>
                      PDFを差し替え
                      <input
                        type="file"
                        accept="application/pdf,.pdf"
                        className="dq-hidden-file"
                        disabled={busy}
                        ref={(el) => {
                          replaceInputs.current[doc.id] = el;
                        }}
                        onChange={(e) => handleReplacePdf(doc, e.target.files?.[0])}
                      />
                    </label>
                    {editingId !== doc.id && (
                      <button type="button" className="dq-btn dq-btn-secondary" onClick={() => startEdit(doc)} disabled={busy}>
                        タイトルを変更
                      </button>
                    )}
                    <button type="button" className="dq-btn dq-btn-danger" onClick={() => handleDelete(doc)} disabled={busy}>
                      削除
                    </button>
                  </div>
                </div>
              </article>
            );
          })}
        </div>
      </section>
    </div>
  );
};

export default DocumentQrPanel;
